import { Fragment } from "react";
import { Modal } from "@/components/ui/modal";
import { Button } from "@/components/ui/button";
import { AlertTriangle } from "lucide-react";
import { cn } from "@/components/utils/utils";
import { useI18n } from "@/hooks/useI18n";

export interface ActivityModalTab {
  key: string;
  label: string;
  content: React.ReactNode;
  /** Icona a sinistra della voce nella navigazione. */
  icon?: React.ReactNode;
  /** Mostra un pallino rosso sulla voce (tab con campi in errore) */
  hasError?: boolean;
  /** Non renderizzata affatto (es. tab Immagini per Flight/Transportation) */
  hidden?: boolean;
  /** Pallino neutro: il tab e' facoltativo ma contiene gia' dei dati. */
  hasContent?: boolean;
  /**
   * Il tab non ha campi obbligatori: resta al suo posto nell'elenco ma in una
   * taglia piu' piccola e in grigio, cosi' si vede a colpo d'occhio cosa serve
   * davvero per salvare.
   */
  optional?: boolean;
  /**
   * Gruppo della voce nella navigazione, separato dagli altri da una riga:
   * 'main' i tab da compilare, 'optional' quelli facoltativi, 'import' le
   * scorciatoie che riempiono il form da un'altra fonte (libreria oggi,
   * ricerca alloggio domani).
   */
  group?: 'main' | 'optional' | 'import';
  /**
   * La voce resta visibile ma non si apre, con un badge "presto": serve a dire
   * che la scorciatoia esiste ed e' in arrivo, invece di farla sparire e
   * ricomparire a ogni rilascio.
   */
  comingSoon?: boolean;
}

interface ActivityModalShellProps {
  isOpen: boolean;
  onClose: () => void;
  icon?: React.ReactNode;
  title: React.ReactNode;
  formId: string;
  onSubmit: (e: React.FormEvent) => void;
  showValidationBanner: boolean;
  tabs: ActivityModalTab[];
  activeTab: string;
  onActiveTabChange: (key: string) => void;
}

/**
 * Chrome condiviso tra le modali attività del builder in creazione/modifica:
 * navigazione a sinistra (riga scorrevole sotto il breakpoint md) e contenuto
 * scrollabile a destra, footer con validation banner. Stesso impianto del
 * modale richieste cliente, così i due percorsi di inserimento si somigliano.
 *
 * Ogni tipo di attività resta proprietario dei propri campi/validazione —
 * passa solo il contenuto già pronto per ogni tab.
 *
 * La sola lettura (apertura da Libreria) NON passa da qui: le modali fanno
 * early-return su <ActivityDetailView /> quando `readOnly` è attivo.
 */
const ActivityModalShell = ({
  isOpen,
  onClose,
  icon,
  title,
  formId,
  onSubmit,
  showValidationBanner,
  tabs,
  activeTab,
  onActiveTabChange,
}: ActivityModalShellProps) => {
  const { t } = useI18n();
  const visibleTabs = tabs.filter(tab => !tab.hidden);
  // Un tab "presto" non e' selezionabile: se `activeTab` lo indica comunque
  // (stato rimasto da prima che venisse sospeso) si ricade sul primo apribile.
  const openableTabs = visibleTabs.filter(tab => !tab.comingSoon);
  const current = openableTabs.find(tab => tab.key === activeTab) ?? openableTabs[0];
  // Una riga di separazione a ogni cambio di gruppo, mai in cima all'elenco.
  const groupRank = (tab: ActivityModalTab) =>
    tab.group === 'import' ? 2 : (tab.group === 'optional' || tab.optional) ? 1 : 0;
  const startsGroup = (index: number) =>
    index > 0 && groupRank(visibleTabs[index]) !== groupRank(visibleTabs[index - 1]);
  // Le scorciatoie stanno in fondo alla colonna: sono azioni, non il percorso
  // di compilazione, e devono restare fuori dal flusso dei tab.
  const startsImportGroup = (index: number) =>
    startsGroup(index) && visibleTabs[index].group === 'import';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        <div className="flex items-center gap-2">
          {icon} {title}
        </div>
      }
      size="lg"
      footer={
        <div className="flex w-full items-center justify-between gap-3">
          <div className="text-xs">
            {showValidationBanner && (
              <span className="flex items-center gap-1 text-red-600">
                <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                {t('modals.requiredFieldsError')}
              </span>
            )}
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" form={formId}>
              {t('common.confirm')}
            </Button>
          </div>
        </div>
      }
    >
      <form id={formId} onSubmit={onSubmit}>
        {/* Altezza fissa: la modale non deve crescere o rimpicciolirsi
            passando da un tab pieno a uno corto (es. Documenti). */}
        <div className="flex flex-col md:flex-row h-[70vh]">
          {/* Navigazione: colonna a sinistra da tablet in su, riga scorrevole su mobile */}
          <nav className="flex md:flex-col gap-1 shrink-0 md:w-44 p-2 md:p-3 border-b md:border-b-0 md:border-r border-gray-200 overflow-x-auto">
            {visibleTabs.map((tab, index) => (
              <Fragment key={tab.key}>
                {startsGroup(index) && (
                  <span
                    className={cn(
                      'shrink-0 self-stretch border-l md:border-l-0 md:border-t border-gray-200 md:my-1.5 mx-1.5 md:mx-1',
                      startsImportGroup(index) && 'md:mt-auto',
                    )}
                    aria-hidden
                  />
                )}
                <button
                  type="button"
                  onClick={() => onActiveTabChange(tab.key)}
                  disabled={tab.comingSoon}
                  className={cn(
                    'flex items-center transition-colors',
                    // La pill impila icona e testo: l'etichetta è una frase, non una
                    // parola, e su una riga sola non starebbe nella colonna.
                    tab.group === 'import'
                      ? 'flex-col gap-1 text-center'
                      : 'gap-2 text-left whitespace-nowrap',
                    // Le scorciatoie non sono sezioni da compilare ma azioni che
                    // riempiono il form: pill con bordo, non voce di navigazione.
                    tab.group === 'import'
                      ? 'justify-center rounded-2xl border px-3 py-2.5 text-xs font-medium leading-tight'
                      : tab.optional
                        ? 'rounded-md px-3 py-1.5 text-xs font-normal'
                        : 'rounded-md px-3 py-2 text-sm font-medium',
                    tab.comingSoon
                      ? tab.group === 'import'
                        ? 'border-dashed border-gray-200 text-gray-400 cursor-not-allowed'
                        : 'text-gray-400 cursor-not-allowed'
                      : tab.key === current?.key
                        ? tab.group === 'import'
                          ? 'border-gray-900 bg-gray-900 text-white'
                          : 'bg-gray-900 text-white'
                        : tab.group === 'import'
                          ? 'border-gray-300 text-gray-600 hover:border-gray-400 hover:text-gray-900'
                          : tab.optional
                            ? 'text-gray-400 hover:bg-gray-100 hover:text-gray-600'
                            : 'text-gray-600 hover:bg-gray-100',
                  )}
                >
                  {tab.icon}
                  <span className={tab.group === 'import' ? undefined : 'flex-1'}>{tab.label}</span>
                  {tab.comingSoon && (
                    <span className="shrink-0 rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium uppercase leading-none tracking-wide text-gray-500">
                      {t('common.soon')}
                    </span>
                  )}
                  {tab.hasError && (
                    <span className="h-1.5 w-1.5 rounded-full bg-red-500 shrink-0" aria-hidden />
                  )}
                  {tab.hasContent && !tab.hasError && (
                    <span
                      className={cn(
                        'h-1.5 w-1.5 rounded-full shrink-0',
                        tab.key === current?.key ? 'bg-white/70' : 'bg-gray-400',
                      )}
                      aria-hidden
                    />
                  )}
                </button>
              </Fragment>
            ))}
          </nav>

          {/* space-y-4: le modali non ancora convertite passano una sequenza di
              Card che si appoggiava alla spaziatura del vecchio TabsContent. */}
          <div className="flex-1 overflow-y-auto space-y-4 p-5 md:p-6">
            {current?.content}
          </div>
        </div>
      </form>
    </Modal>
  );
};

export default ActivityModalShell;
